"use client";

import { useMemo, useCallback } from "react";
import { SUPPORTED_EXCHANGES } from "@cede/types";
import { useAuthenticationProvider } from "@/hooks/authentication/useAuthenticationProvider";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useAccount } from "wagmi";

export type CexPerpsSessionStatus =
  | "checking-wallet"
  | "needs-wallet"
  | "needs-approval"
  | "checking-activation"
  | "ready";

/**
 * Generic hook for managing CEX perps exchange connection via API key input.
 *
 * Uses the standard Hub CEX account connection flow:
 * 1. User creates API credentials on the exchange
 * 2. User enters credentials in the approval form
 * 3. Credentials are sent to backend via standard addExchangeAccount flow
 */
export function useCexPerpsConnection(exchangeId: string = SUPPORTED_EXCHANGES.BITGET) {
  const { hubUser, walletStatus, addExchangeAccount } = useHubUser();
  const { address: connectedAddress, isConnected: isWalletConnected } = useAccount();
  const { authenticated } = useAuthenticationProvider();
  const queryClient = useQueryClient();

  const accounts = useMemo(
    () => hubUser?.exchangeAccounts?.filter((a) => a.exchangeId === exchangeId) ?? [],
    [hubUser?.exchangeAccounts, exchangeId],
  );

  const hasAccount = accounts.length > 0;

  const {
    mutateAsync: connect,
    isPending: isConnecting,
    error: connectionError,
    reset: resetConnectionError,
  } = useMutation({
    mutationFn: async (params: { apiKey: string; secretKey: string; passphrase: string }) => {
      await addExchangeAccount({
        selectedCex: exchangeId,
        authMethod: "api-keys",
        apiKey: params.apiKey,
        secretKey: params.secretKey,
        uid: undefined,
        password: params.passphrase,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      enqueueSnackbar("Account connected successfully", { variant: "success" });
    },
    onError: (error) => {
      const msg = error instanceof Error ? error.message : "Failed to connect exchange";
      enqueueSnackbar(msg, { variant: "error" });
    },
  });

  const approveAgent = useCallback(
    (params?: Record<string, unknown>) => {
      const apiKey = params?.apiKey as string | undefined;
      const secretKey = params?.secretKey as string | undefined;
      const passphrase = params?.passphrase as string | undefined;
      if (!apiKey || !secretKey || !passphrase) {
        return Promise.reject(new Error("API credentials required"));
      }
      return connect({ apiKey, secretKey, passphrase });
    },
    [connect],
  );

  const sessionStatus: CexPerpsSessionStatus =
    walletStatus === "initializing" || walletStatus === "connecting"
      ? "checking-wallet"
      : !authenticated
        ? "needs-wallet"
        : isConnecting
          ? "checking-activation"
          : hasAccount
            ? "ready"
            : "needs-approval";

  return {
    connectedAddress,
    isWalletConnected: isWalletConnected && authenticated,
    accounts,
    hasAccount,
    connect,
    isConnecting,
    connectionError,
    resetConnectionError,
    sessionStatus,
    agentData: {
      accountId: accounts[0]?.id ?? undefined,
      agentAddress: accounts[0]?.publicApiKey ?? "",
      agentName: "",
    },
    hasAgentData: hasAccount,
    isLoadingAgent: false,
    isCheckingEligibility: false,
    isNotEligibleError: false,
    eligibilityData: { eligible: true } as const,
    isEligible: true,
    approveAgent,
    isApproving: isConnecting,
    approvalError: connectionError,
    resetApprovalError: resetConnectionError,
    updateHyperliquidAgentStatus: async () => {},
    isUpdatingAgentStatus: false,
    isLoading: isConnecting,
    walletStatus,
    authenticated,
  };
}
